import { type FC, useState, useCallback, useRef, useEffect } from "react";
import type { AgentMessageFragmentClarifyingQuestions } from "@/hooks/use-stream-conversation";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { HelpCircle } from "lucide-react";
import type { Answer } from "@/components/ai/clarifying-questions-utils";
import { PromptInputModifierEnterKeyBinding } from "@/utils/platform";
import { isTouchDevice } from "@/utils/is-touch-device";

/** Converts a 0-based index to a letter: 0→"A", 1→"B", 2→"C", etc. */
function choiceLetter(index: number): string {
    return String.fromCharCode(65 + index);
}

type FragmentClarifyingQuestionsProps = {
    fragment: AgentMessageFragmentClarifyingQuestions;
    onSubmit: (answers: Map<string, Answer>) => void;
};

export const FragmentClarifyingQuestions: FC<FragmentClarifyingQuestionsProps> = ({ fragment, onSubmit }) => {
    const { questions } = fragment;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Map<string, Answer>>(new Map());
    const [otherText, setOtherText] = useState<Map<string, string>>(new Map());
    const [submitted, setSubmitted] = useState(false);
    const [focusedChoiceIndex, setFocusedChoiceIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const otherInputRef = useRef<HTMLInputElement>(null);

    const currentQuestion = questions[currentIndex];
    const totalQuestions = questions.length;
    const isLastQuestion = currentIndex === totalQuestions - 1;

    const currentAnswer = answers.get(currentQuestion?.questionId);
    const currentOtherText = otherText.get(currentQuestion?.questionId) || "";
    const isOtherSelected = currentAnswer?.type === "other";
    const hasValidAnswer = currentAnswer && (currentAnswer.type !== "other" || currentAnswer.value?.trim());

    const handleSelectChoice = useCallback((questionId: string, choiceText: string) => {
        setFocusedChoiceIndex(null);
        setAnswers((prev) => {
            const next = new Map(prev);
            next.set(questionId, { type: "choice", value: choiceText });
            return next;
        });
    }, []);

    const handleSelectOther = useCallback((questionId: string) => {
        setFocusedChoiceIndex(null);
        setAnswers((prev) => {
            const next = new Map(prev);
            next.set(questionId, { type: "other", value: "" });
            return next;
        });
    }, []);

    const handleOtherTextChange = useCallback((questionId: string, text: string) => {
        setOtherText((prev) => {
            const next = new Map(prev);
            next.set(questionId, text);
            return next;
        });
        setAnswers((prev) => {
            const next = new Map(prev);
            next.set(questionId, { type: "other", value: text });
            return next;
        });
    }, []);

    const handleSkip = useCallback(() => {
        const questionId = currentQuestion?.questionId;
        const updatedAnswers = new Map(answers);
        if (questionId) {
            updatedAnswers.set(questionId, { type: "skipped" });
            setAnswers(updatedAnswers);
        }
        if (isLastQuestion) {
            setSubmitted(true);
            onSubmit(updatedAnswers);
        } else {
            setCurrentIndex((prev) => prev + 1);
        }
    }, [currentQuestion?.questionId, isLastQuestion, answers, onSubmit]);

    const handleContinue = useCallback(() => {
        if (isLastQuestion) {
            setSubmitted(true);
            onSubmit(answers);
        } else {
            setCurrentIndex((prev) => prev + 1);
        }
    }, [isLastQuestion, answers, onSubmit]);

    const handleOtherInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                if (hasValidAnswer) {
                    handleContinue();
                }
                return;
            }

            e.stopPropagation();
        },
        [handleContinue, hasValidAnswer],
    );

    const handleNavigate = useCallback(
        (direction: "up" | "down") => {
            if (direction === "up" && currentIndex > 0) {
                setCurrentIndex((prev) => prev - 1);
            } else if (direction === "down" && currentIndex < totalQuestions - 1) {
                setCurrentIndex((prev) => prev + 1);
            }
        },
        [currentIndex, totalQuestions],
    );

    const getTotalChoices = useCallback(() => {
        if (!currentQuestion) return 0;
        return currentQuestion.choices.length + (currentQuestion.allowOther ? 1 : 0);
    }, [currentQuestion]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const totalChoices = getTotalChoices();
            if (totalChoices === 0) return;

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setFocusedChoiceIndex((prev) => {
                        if (prev === null) return 0;
                        return (prev + 1) % totalChoices;
                    });
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setFocusedChoiceIndex((prev) => {
                        if (prev === null) return totalChoices - 1;
                        return (prev - 1 + totalChoices) % totalChoices;
                    });
                    break;
                case "Enter":
                    e.preventDefault();
                    if (e.metaKey || e.ctrlKey) {
                        if (hasValidAnswer) {
                            handleContinue();
                        }
                    } else if (focusedChoiceIndex !== null && currentQuestion) {
                        const isOtherIndex = focusedChoiceIndex === currentQuestion.choices.length;
                        if (isOtherIndex && currentQuestion.allowOther) {
                            handleSelectOther(currentQuestion.questionId);
                        } else if (focusedChoiceIndex < currentQuestion.choices.length) {
                            const choice = currentQuestion.choices[focusedChoiceIndex];
                            handleSelectChoice(currentQuestion.questionId, choice.text);
                        }
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    handleSkip();
                    break;
            }
        },
        [
            getTotalChoices,
            focusedChoiceIndex,
            currentQuestion,
            hasValidAnswer,
            handleContinue,
            handleSelectOther,
            handleSelectChoice,
            handleSkip,
        ],
    );

    useEffect(() => {
        setFocusedChoiceIndex(null);
        containerRef.current?.focus();
    }, [currentIndex]);

    // Auto-focus the "Other" text input when it becomes selected
    useEffect(() => {
        if (isOtherSelected) {
            // Defer to next frame so the input element is rendered
            requestAnimationFrame(() => otherInputRef.current?.focus());
        }
    }, [isOtherSelected]);

    // setTimeout ensures focus wins over ChatInput's autoFocus
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            containerRef.current?.focus();
        }, 0);
        return () => clearTimeout(timeoutId);
    }, []);

    if (submitted) {
        return (
            <div className="px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-content-secondary">
                    <HelpCircle className="size-4" />
                    <span>Questions answered</span>
                </div>
            </div>
        );
    }

    if (!currentQuestion) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className="overflow-hidden outline-none focus:border-none focus:outline-none focus:ring-0"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div className="flex items-center justify-between border-b border-border-subtle px-4 py-1.5">
                <div className="flex items-center gap-2 text-content-secondary">
                    <HelpCircle className="size-4" />
                    <span className="text-sm font-medium">Questions</span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => handleNavigate("up")}
                        disabled={currentIndex === 0}
                        className={cn(
                            "rounded p-px transition-colors",
                            currentIndex === 0
                                ? "cursor-not-allowed text-content-tertiary"
                                : "text-content-secondary hover:bg-surface-tertiary hover:text-content-primary",
                        )}
                        aria-label="Previous question"
                        data-tracking-id="clarifying-questions-previous"
                    >
                        <IconChevronDown size="sm" className="rotate-180" />
                    </button>
                    <span className="min-w-[3.5rem] text-center text-sm text-content-secondary">
                        {currentIndex + 1} of {totalQuestions}
                    </span>
                    <button
                        type="button"
                        onClick={() => handleNavigate("down")}
                        disabled={currentIndex === totalQuestions - 1}
                        className={cn(
                            "rounded p-px transition-colors",
                            currentIndex === totalQuestions - 1
                                ? "cursor-not-allowed text-content-tertiary"
                                : "text-content-secondary hover:bg-surface-tertiary hover:text-content-primary",
                        )}
                        aria-label="Next question"
                        data-tracking-id="clarifying-questions-next"
                    >
                        <IconChevronDown size="sm" />
                    </button>
                </div>
            </div>

            <div className="px-4 pb-2" aria-live="polite" aria-atomic="true">
                <div className="mb-2">
                    <span className="text-sm font-medium text-content-primary">{currentIndex + 1}. </span>
                    <span className="select-text text-sm font-medium text-content-primary">
                        {currentQuestion.question}
                    </span>
                </div>

                <div className="space-y-0.5">
                    {currentQuestion.choices.map((choice, index) => {
                        const isSelected = currentAnswer?.type === "choice" && currentAnswer.value === choice.text;
                        const isFocused = focusedChoiceIndex === index;
                        return (
                            <button
                                key={choice.label}
                                type="button"
                                onClick={() => handleSelectChoice(currentQuestion.questionId, choice.text)}
                                className={cn(
                                    "flex w-full min-w-0 items-center gap-2 rounded px-2 py-1 text-left outline-none transition-colors",
                                    isSelected
                                        ? "bg-surface-brand-subtle text-content-primary"
                                        : isFocused
                                          ? "bg-surface-brand-subtle ring-1 ring-border-brand"
                                          : "hover:bg-surface-secondary",
                                )}
                                data-tracking-id="clarifying-questions-choice"
                            >
                                <span
                                    className={cn(
                                        "flex size-4 shrink-0 items-center justify-center overflow-hidden rounded text-[11px] font-medium",
                                        isSelected || isFocused
                                            ? "bg-content-brand text-content-primary-inverted"
                                            : "bg-surface-tertiary text-content-secondary",
                                    )}
                                >
                                    {choiceLetter(index)}
                                </span>
                                <span className="min-w-0 flex-1 whitespace-normal break-words text-base text-content-primary">
                                    {choice.text}
                                </span>
                            </button>
                        );
                    })}

                    {currentQuestion.allowOther &&
                        (() => {
                            const otherIndex = currentQuestion.choices.length;
                            const isOtherFocused = focusedChoiceIndex === otherIndex;
                            return (
                                <button
                                    type="button"
                                    onClick={() => !isOtherSelected && handleSelectOther(currentQuestion.questionId)}
                                    className={cn(
                                        "flex w-full min-w-0 items-center gap-2 rounded px-2 py-1 text-left outline-none transition-colors",
                                        isOtherSelected
                                            ? "bg-surface-brand-subtle text-content-primary"
                                            : isOtherFocused
                                              ? "cursor-pointer bg-surface-brand-subtle ring-1 ring-border-brand"
                                              : "cursor-pointer hover:bg-surface-secondary",
                                    )}
                                    data-tracking-id="clarifying-questions-other"
                                >
                                    <span
                                        className={cn(
                                            "flex size-4 shrink-0 items-center justify-center overflow-hidden rounded text-[11px] font-medium",
                                            isOtherSelected || isOtherFocused
                                                ? "bg-content-brand text-content-primary-inverted"
                                                : "bg-surface-tertiary text-content-secondary",
                                        )}
                                    >
                                        {choiceLetter(currentQuestion.choices.length)}
                                    </span>
                                    {isOtherSelected ? (
                                        <input
                                            ref={otherInputRef}
                                            type="text"
                                            placeholder="Type your answer..."
                                            value={currentOtherText}
                                            onChange={(e) =>
                                                handleOtherTextChange(currentQuestion.questionId, e.target.value)
                                            }
                                            className="min-w-0 flex-1 border-none bg-transparent text-base text-content-primary outline-none placeholder:text-content-muted focus:ring-0"
                                            autoFocus
                                            onKeyDown={handleOtherInputKeyDown}
                                        />
                                    ) : (
                                        <span className="text-base text-content-tertiary">Other...</span>
                                    )}
                                </button>
                            );
                        })()}
                </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-1.5">
                <Button variant="ghost" size="xs" onClick={handleSkip} data-tracking-id="clarifying-questions-skip">
                    Skip
                </Button>
                <Button
                    variant={hasValidAnswer ? "primary" : "secondary"}
                    size="xs"
                    onClick={handleContinue}
                    disabled={!hasValidAnswer}
                    data-tracking-id="clarifying-questions-continue"
                >
                    Continue
                    {!isTouchDevice() && (
                        <span className="ml-1 opacity-60">
                            <KeyboardCombo keys={PromptInputModifierEnterKeyBinding} />
                        </span>
                    )}
                </Button>
            </div>
        </div>
    );
};
